/**
 * Copyright 2013 Google, Inc.
 * Copyright 2015 Daishinsha Inc.
 * Copyright 2019 Vivliostyle Foundation
 *
 * Vivliostyle.js is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Vivliostyle.js is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Vivliostyle.js.  If not, see <http://www.gnu.org/licenses/>.
 *
 * @fileoverview CssStyler - Apply CSS cascade to a document incrementally and
 * cache the result.
 */
import * as Base from "./base";
import * as Break from "./break";
import * as CmykStore from "./cmyk-store";
import * as CounterStyle from "./counter-style";
import * as Css from "./css";
import * as CssCascade from "./css-cascade";
import * as CssParser from "./css-parser";
import * as CssProp from "./css-prop";
import * as CssValidator from "./css-validator";
import * as Display from "./display";
import * as Exprs from "./exprs";
import * as LayoutProcessor from "./layout-processor";
import * as Vtree from "./vtree";
import { CssStyler, XmlDoc } from "./types";

export class SlipRange {
  endStuckFixed: number;
  endFixed: number;
  endSlipped: number;

  constructor(endStuckFixed, endFixed, endSlipped) {
    this.endStuckFixed = endStuckFixed;
    this.endFixed = endFixed;
    this.endSlipped = endSlipped;
  }
}

/**
 * Maps all ints in a range ("fixed") to ints with slippage ("slipped")
 */
export class SlipMap {
  map = [] as SlipRange[];

  getMaxSlipped(): number {
    if (this.map.length == 0) {
      return 0;
    }
    const range = this.map[this.map.length - 1];
    return range.endSlipped;
  }

  addStuckRange(endFixed: number): void {
    if (this.map.length == 0) {
      this.map.push(new SlipRange(endFixed, endFixed, endFixed));
    } else {
      const range = this.map[this.map.length - 1];
      const endSlipped = range.endSlipped + endFixed - range.endFixed;
      if (range.endFixed == range.endStuckFixed) {
        range.endFixed = endFixed;
        range.endStuckFixed = endFixed;
        range.endSlipped = endSlipped;
      } else {
        this.map.push(new SlipRange(endFixed, endFixed, endSlipped));
      }
    }
  }

  addSlippedRange(endFixed: number): void {
    if (this.map.length == 0) {
      this.map.push(new SlipRange(endFixed, 0, 0));
    } else {
      this.map[this.map.length - 1].endFixed = endFixed;
    }
  }

  slippedByFixed(fixed: number): number {
    const index = Base.binarySearch(
      this.map.length,
      (index) => fixed <= this.map[index].endFixed,
    );
    const range = this.map[index];
    return range.endSlipped - Math.max(0, range.endStuckFixed - fixed);
  }

  /**
   * Smallest fixed for a given slipped.
   */
  fixedBySlipped(slipped: number): number {
    const index = Base.binarySearch(
      this.map.length,
      (index) => slipped <= this.map[index].endSlipped,
    );
    const range = this.map[index];
    return range.endStuckFixed - (range.endSlipped - slipped);
  }
}

type CounterSnapshot = {
  offset: number;
  counters: CssCascade.CounterValues;
  changes: string[];
  changeTypes?: { [key: string]: "reset" | "set" | "increment" };
};

function cloneCounterValues(
  counters: CssCascade.CounterValues,
): CssCascade.CounterValues {
  const result = {} as CssCascade.CounterValues;
  Object.keys(counters).forEach((name) => {
    result[name] = Array.from(counters[name]);
  });
  return result;
}

export interface FlowListener {
  encounteredFlowChunk(flowChunk: Vtree.FlowChunk, flow: Vtree.Flow): void;
}

export interface AbstractStyler extends CssStyler.AbstractStyler {}

/**
 * Represent a box generated by a (pseudo)element. When constructed, a box
 * corresponding to `::before` pseudoelement is also constructed and stored in
 * `beforeBox` property, whereas one corresponding `::after` pseudoelement is
 * not constructed and `afterBox` property is `null`. `afterBox` is constructed
 * by `buildAfterPseudoElementBox` method.
 * @param style Cascaded style values for the box.
 * @param offset The start offset of the box. It coincides with the start offset
 *     of the element if the box is generated by the element or the `::before`
 *     pseudoelement. When the box corresponds to the `::after` pseudoelement,
 *     the offset is just after the content before the `::after` pseudoelement.
 * @param isRoot True if the box is generated by the root element (not
 *     pseudoelement).
 * @param flowChunk FlowChunk to which the box belongs to.
 * @param atBlockStart True if the box is right after the block start edge.
 * @param atFlowStart True if the box is right after the flow start.
 * @param isParentBoxDisplayed True if the parent box has a displayed box.
 */
export class Box {
  flowName: string;
  isBlockValue: boolean | null = null;
  hasBoxValue: boolean | null = null;
  styleValues = {} as { [key: string]: Css.Val | null };
  beforeBox: Box | null = null;
  afterBox: Box | null = null;
  breakBefore: string | null = null;

  constructor(
    public readonly context: Exprs.Context,
    public readonly style: CssCascade.ElementStyle,
    public readonly offset: number,
    public readonly isRoot: boolean,
    public readonly flowChunk: Vtree.FlowChunk,
    public readonly atBlockStart: boolean,
    public readonly atFlowStart: boolean,
    public readonly isParentBoxDisplayed: boolean,
  ) {
    this.flowName = flowChunk.flowName;
    if (this.hasBox()) {
      const pseudoMap = style["_pseudos"];
      if (pseudoMap) {
        if (pseudoMap["before"]) {
          const beforeBox = new Box(
            context,
            pseudoMap["before"],
            offset,
            false,
            flowChunk,
            this.isBlock(),
            atFlowStart,
            true,
          );
          const beforeContent = beforeBox.styleValue("content");
          if (Vtree.nonTrivialContent(beforeContent)) {
            this.beforeBox = beforeBox;
            this.breakBefore = beforeBox.breakBefore;
          }
        }
      }
    }
    this.breakBefore = Break.resolveEffectiveBreakValue(
      this.getBreakValue("before"),
      this.breakBefore,
    );
    if (this.atFlowStart && Break.isForcedBreakValue(this.breakBefore)) {
      flowChunk.breakBefore = Break.resolveEffectiveBreakValue(
        flowChunk.breakBefore,
        this.breakBefore,
      );
    }
  }

  /**
   * Build a box corresponding to `::after` pseudoelement and stores it in
   * `afterBox` property.
   * @param offset The start offset of the `::after` pseudoelement box, which is
   *     just after the content before the `::after` pseudoelement.
   * @param atBlockStart True if the box is right after the block start edge.
   * @param atFlowStart True if the box is right after the flow start.
   */
  buildAfterPseudoElementBox(
    offset: number,
    atBlockStart: boolean,
    atFlowStart: boolean,
  ) {
    if (this.hasBox()) {
      const pseudoMap = this.style["_pseudos"];
      if (pseudoMap) {
        if (pseudoMap["after"]) {
          const afterBox = new Box(
            this.context,
            pseudoMap["after"],
            offset,
            false,
            this.flowChunk,
            atBlockStart,
            atFlowStart,
            true,
          );
          const afterContent = afterBox.styleValue("content");
          if (Vtree.nonTrivialContent(afterContent)) {
            this.afterBox = afterBox;
          }
        }
      }
    }
  }

  styleValue(name: string, defaultValue?: Css.Val): Css.Val | null {
    if (!(name in this.styleValues)) {
      const cv = this.style[name] as CssCascade.CascadeValue;
      this.styleValues[name] = cv
        ? cv.evaluate(this.context, name)
        : defaultValue || null;
    }
    return this.styleValues[name];
  }

  displayValue(): Css.Val {
    return this.styleValue("display", Css.ident.inline);
  }

  isBlock(): boolean {
    if (this.isBlockValue === null) {
      const display = this.displayValue() as Css.Ident;
      const position = this.styleValue("position") as Css.Ident;
      const float = this.styleValue("float") as Css.Ident;
      this.isBlockValue = Display.isBlock(
        display,
        position,
        float,
        this.isRoot,
      );
    }
    return this.isBlockValue;
  }

  hasBox(): boolean {
    if (this.hasBoxValue === null) {
      this.hasBoxValue =
        this.isParentBoxDisplayed &&
        this.displayValue() !== Css.ident.none &&
        !Display.isRunning(this.styleValue("position"));
    }
    return this.hasBoxValue;
  }

  getBreakValue(edge: string): string | null {
    let breakValue: string | null = null;
    if (this.isBlock()) {
      const val = this.styleValue(`break-${edge}`);
      if (val) {
        breakValue = val.toString();
      }
    }
    return breakValue;
  }
}

/**
 * Manages boxes generated by elements as a stack.
 */
export class BoxStack {
  stack = [] as Box[];
  atBlockStart: boolean = true; // indicates if the next pushed box will be at the block start
  atFlowStart: boolean = true; // indicates if the next pushed box will be at the flow start
  atStartStack = [] as { atBlockStart: boolean; atFlowStart: boolean }[]; // pushed when a new flow starts

  constructor(public readonly context: Exprs.Context) {}

  /**
   * Returns if the stack is empty.
   */
  empty(): boolean {
    return this.stack.length === 0;
  }

  /**
   * Returns the last box in the stack.
   */
  lastBox(): Box | undefined {
    return this.stack[this.stack.length - 1];
  }

  /**
   * Returns the flow name of the last box in the stack.
   */
  lastFlowName(): string | null {
    const lastBox = this.lastBox();
    return lastBox ? lastBox.flowChunk.flowName : null;
  }

  /**
   * Returns if the last box in the stack is displayed.
   */
  isCurrentBoxDisplayed(): boolean {
    return this.stack.every((box) => box.displayValue() !== Css.ident.none);
  }

  /**
   * Create a new box and push it to the stack.
   * @param style Cascaded style values for the box.
   * @param offset The start offset of the box.
   * @param isRoot True if the box is generated by the root element
   * @param newFlowChunk Specify if the element is a flow element (i.e. the
   *     element is specified a new `flow-into` value)
   */
  push(
    style: CssCascade.ElementStyle,
    offset: number,
    isRoot: boolean,
    newFlowChunk?: Vtree.FlowChunk,
  ): Box {
    const lastBox = this.lastBox();
    if (newFlowChunk && lastBox && newFlowChunk.flowName !== lastBox.flowName) {
      this.atStartStack.push({
        atBlockStart: this.atBlockStart,
        atFlowStart: this.atFlowStart,
      });
    }
    const flowChunk = newFlowChunk || lastBox.flowChunk;
    const isAtFlowStart = this.atFlowStart || !!newFlowChunk;
    const isParentBoxDisplayed = this.isCurrentBoxDisplayed();
    const box = new Box(
      this.context,
      style,
      offset,
      isRoot,
      flowChunk,
      isAtFlowStart || this.atBlockStart,
      isAtFlowStart,
      isParentBoxDisplayed,
    );
    this.stack.push(box);
    this.atBlockStart = box.hasBox()
      ? !box.beforeBox && box.isBlock()
      : this.atBlockStart;
    this.atFlowStart = box.hasBox()
      ? !box.beforeBox && isAtFlowStart
      : this.atFlowStart;
    return box;
  }

  encounteredTextNode(node: Node) {
    const box = this.lastBox();
    if (
      (node.nodeType === Node.TEXT_NODE ||
        node.nodeType === Node.CDATA_SECTION_NODE) &&
      (this.atBlockStart || this.atFlowStart) &&
      box.hasBox()
    ) {
      const whitespaceValue = box
        .styleValue("white-space", Css.ident.normal)
        .toString();
      const whitespace = Vtree.whitespaceFromPropertyValue(whitespaceValue);
      if (whitespace && !Vtree.canIgnore(node, whitespace)) {
        this.atBlockStart = false;
        this.atFlowStart = false;
      }
    }
  }

  /**
   * Pop the last box.
   */
  pop(offset: number): Box {
    const box = this.stack.pop();
    box.buildAfterPseudoElementBox(offset, this.atBlockStart, this.atFlowStart);
    if (this.atFlowStart && box.afterBox) {
      const breakBefore = box.afterBox.getBreakValue("before");
      box.flowChunk.breakBefore = Break.resolveEffectiveBreakValue(
        box.flowChunk.breakBefore,
        breakBefore,
      );
    }
    const parent = this.lastBox();
    if (parent) {
      if (parent.flowName === box.flowName) {
        if (box.hasBox()) {
          this.atBlockStart = this.atFlowStart = false;
        }
      } else {
        const atStart = this.atStartStack.pop();
        this.atBlockStart = atStart.atBlockStart;
        this.atFlowStart = atStart.atFlowStart;
      }
    }
    return box;
  }

  /**
   * Find the start offset of the nearest block start edge to which the
   * `break-before` value of the box should be propagated. This method can be
   * called when after pushing the box into the stack or after popping the box
   * out of the stack.
   */
  nearestBlockStartOffset(box: Box): number {
    if (!box.atBlockStart) {
      return box.offset;
    }
    let i = this.stack.length - 1;
    let parent = this.stack[i];

    // When called just after the box is popped out, the last box in the stack
    // is different from the box and it is the parent of the box. When called
    // after the box is pushed, the last box in the stack is identical to the
    // box and the parent of the box is a box right before the specified box.
    if (parent === box) {
      i--;
      parent = this.stack[i];
    }
    while (i >= 0) {
      if (parent.flowName !== box.flowName) {
        return box.offset;
      }
      if (!parent.atBlockStart) {
        return parent.offset;
      }
      if (parent.isRoot) {
        return parent.offset;
      }
      box = parent;
      parent = this.stack[--i];
    }
    throw new Error("No block start offset found!");
  }
}

export class StyleStore implements CssCascade.StyleReader {
  private readonly map: { [key: string]: CssCascade.ElementStyle } = {};

  constructor(private readonly xmldoc: XmlDoc.XMLDocHolder) {}

  setAt(offset: number, style: CssCascade.ElementStyle): void {
    this.map[`e${offset}`] = style;
  }

  styleOf(element: Element): CssCascade.ElementStyle {
    return this.map[`e${this.xmldoc.getElementOffset(element)}`];
  }
}

export class Styler implements AbstractStyler {
  root: Base.ChildElement;
  last: Base.RootBoundCursor | null;
  rootStyle = {} as CssCascade.ElementStyle;
  /**
   * writing-mode and direction values propagated from the body element to
   * the root element. Per CSS Writing Modes spec, this propagation is done
   * on used values rather than computed values, so these values must not be
   * inherited by the page context. (Issue #1122)
   */
  bodyPropagatedStyle = {} as CssCascade.ElementStyle;
  styles: StyleStore;
  counterSnapshots: CounterSnapshot[] = [];
  flows = {} as { [key: string]: Vtree.Flow };
  flowChunks = [] as Vtree.FlowChunk[];
  flowListener: FlowListener | null = null;
  flowToReach: string | null = null;
  idToReach: string | null = null;
  cascade: CssCascade.CascadeInstance;
  // the last element the cascade opened, kept across pops: vgen reads it when
  // it re-evaluates generated content
  elementWindow: CssCascade.ElementCascadeInstance;
  offsetMap: SlipMap;
  primary: boolean = true;
  primaryStack = [] as boolean[];
  rootBackgroundAssigned: boolean = false;
  rootLayoutAssigned: boolean = false;
  lastOffset: number;
  breakBeforeValues = {} as { [key: number]: string | null };
  boxStack: BoxStack;
  bodyReached: boolean = true;

  constructor(
    public readonly xmldoc: XmlDoc.XMLDocHolder,
    cascade: CssCascade.Cascade,
    public readonly scope: Exprs.LexicalScope,
    public readonly context: Exprs.Context,
    public readonly primaryFlows: { [key: string]: boolean },
    public readonly validatorSet: CssValidator.ValidatorSet,
    public readonly counterListener: CssCascade.CounterListener,
    counterResolver: CssCascade.CounterResolver,
    counterStyleStore: CounterStyle.CounterStyleStore,
    cmykStore: CmykStore.CmykStore,
    mergeValidatorSet: CssValidator.ValidatorSet | null,
  ) {
    this.root = xmldoc.root;
    this.styles = new StyleStore(xmldoc);
    this.last = Base.RootBoundCursor.atRoot(this.root);
    this.cascade = cascade.createInstance(
      context,
      counterListener,
      counterResolver,
      counterStyleStore,
      cmykStore,
      this.root,
      scope,
      validatorSet,
      this.styles,
      mergeValidatorSet,
    );
    this.offsetMap = new SlipMap();
    const rootOffset = xmldoc.getElementOffset(this.root);
    this.lastOffset = rootOffset;
    this.boxStack = new BoxStack(context);
    this.offsetMap.addStuckRange(rootOffset);
    const style = this.getAttrStyle(this.root);
    this.elementWindow = this.cascade.pushElement(this.root, style, rootOffset);
    this.recordCounterSnapshot(
      rootOffset,
      this.cascade.counters,
      this.cascade.lastCounterChanges,
      this.cascade.lastCounterChangeTypes,
    );
    this.postprocessTopStyle(style, false);
    this.determineRootSizes(style);
    switch (this.root.namespaceURI) {
      case Base.NS.XHTML:
        this.bodyReached = false;
        break;
    }
    this.primaryStack.push(true);
    this.styles.setAt(rootOffset, style);
    this.lastOffset++;
    this.replayFlowElementsFromOffset(-1);
  }

  hasProp(
    style: CssCascade.ElementStyle,
    map: CssValidator.ValueMap,
    name: string,
  ): boolean {
    const cascVal = style[name] as CssCascade.CascadeValue;
    return cascVal && cascVal.evaluate(this.context) !== map[name];
  }

  transferPropsToRoot(
    srcStyle: CssCascade.ElementStyle,
    map: CssValidator.ValueMap,
  ): void {
    for (const pname in map) {
      const cascval = srcStyle[pname];
      if (cascval) {
        this.rootStyle[pname] = cascval;
        delete srcStyle[pname];
      } else {
        const val = map[pname];
        if (val) {
          this.rootStyle[pname] = new CssCascade.CascadeValue(
            val,
            CssParser.SPECIFICITY_AUTHOR,
          );
        }
      }
    }
  }

  /**
   * Transfer properties that should be applied on the container (partition)
   * level to this.rootStyle.
   * @param elemStyle (source) element style
   */
  postprocessTopStyle(
    elemStyle: CssCascade.ElementStyle,
    isBody: boolean,
  ): void {
    if (isBody) {
      for (const propName of ["writing-mode", "direction"]) {
        if (elemStyle[propName] && !this.rootStyle[propName]) {
          // Copy it over, but keep it at the body element as well.
          this.rootStyle[propName] = elemStyle[propName];
          // Record the value propagated from the body element so that it
          // can be excluded from the page context inheritance. (Issue #1122)
          this.bodyPropagatedStyle[propName] = elemStyle[propName];
        }
      }
    } else {
      for (const propName in elemStyle) {
        if (CssCascade.isInherited(propName)) {
          this.rootStyle[propName] = elemStyle[propName];
        }
      }
    }
    if (!this.rootBackgroundAssigned) {
      const backgroundColor = this.hasProp(
        elemStyle,
        this.validatorSet.backgroundProps,
        "background-color",
      )
        ? (elemStyle["background-color"] as CssCascade.CascadeValue).evaluate(
            this.context,
          )
        : null;
      const backgroundImage = this.hasProp(
        elemStyle,
        this.validatorSet.backgroundProps,
        "background-image",
      )
        ? (elemStyle["background-image"] as CssCascade.CascadeValue).evaluate(
            this.context,
          )
        : null;
      if (
        (backgroundColor && !Css.isDefaultingValue(backgroundColor)) ||
        (backgroundImage && !Css.isDefaultingValue(backgroundImage))
      ) {
        this.transferPropsToRoot(elemStyle, this.validatorSet.backgroundProps);
        // background-position-x/-y are not part of the `background` shorthand
        // grammar, so they are absent from backgroundProps. Move them only when
        // the source style actually has them: transferPropsToRoot assigns a
        // default for every property of the map, and a default axis value here
        // would override the background-position moved just above.
        for (const pname of [
          "background-position-x",
          "background-position-y",
        ]) {
          const cascval = elemStyle[pname];
          if (cascval) {
            this.rootStyle[pname] = cascval;
            delete elemStyle[pname];
          }
        }
        this.rootBackgroundAssigned = true;
      }
    }
    if (!this.rootLayoutAssigned) {
      for (let i = 0; i < columnProps.length; i++) {
        if (
          this.hasProp(elemStyle, this.validatorSet.layoutProps, columnProps[i])
        ) {
          this.transferPropsToRoot(elemStyle, this.validatorSet.layoutProps);
          this.rootLayoutAssigned = true;
          break;
        }
      }
    }
  }

  private determineRootSizes(elemStyle: CssCascade.ElementStyle): void {
    const fontSize = elemStyle["font-size"] as CssCascade.CascadeValue;
    let isRelativeFontSize = true;
    if (fontSize && !Css.isDefaultingValue(fontSize.value)) {
      const evaluated = fontSize.evaluate(this.context);
      const val = this.resolveRootSizingCalc(evaluated);
      const fromRelativeCalc =
        evaluated instanceof Css.Func && val instanceof Css.Numeric;
      if (val instanceof Css.Numeric) {
        let px = val.num;
        switch (val.unit) {
          case "em":
          case "rem":
            px *= this.context.initialFontSize;
            break;
          case "%":
            px *= this.context.initialFontSize / 100;
            break;
          case "lh":
          case "rlh":
            px *= this.context.rootLineHeight;
            break;
          default: {
            const unitSize = Exprs.defaultUnitSizes[val.unit];
            if (unitSize) {
              px *= unitSize;
            }
            isRelativeFontSize = fromRelativeCalc;
          }
        }
        this.context.rootFontSize = px;
        this.context.isRelativeRootFontSize = isRelativeFontSize;
      }
    }
    const rootFontSize =
      this.context.rootFontSize ?? this.context.initialFontSize;
    const lineHeight = elemStyle["line-height"] as CssCascade.CascadeValue;
    let rootLineHeight: number | null = null;
    let fromRelativeCalc = false;
    if (lineHeight && !Css.isDefaultingValue(lineHeight.value)) {
      const evaluated = lineHeight.evaluate(this.context);
      const val = this.resolveRootSizingCalc(evaluated);
      fromRelativeCalc =
        evaluated instanceof Css.Func && val instanceof Css.Numeric;
      if (val instanceof Css.Num) {
        rootLineHeight = val.num * rootFontSize;
      } else if (val instanceof Css.Numeric) {
        let px = val.num;
        switch (val.unit) {
          case "em":
          case "rem":
            px *= rootFontSize;
            break;
          case "%":
            px *= rootFontSize / 100;
            break;
          case "lh":
          case "rlh":
            px *= this.context.rootLineHeight;
            break;
          default: {
            const unitSize = Exprs.defaultUnitSizes[val.unit];
            if (unitSize) {
              px *= unitSize;
            }
          }
        }
        rootLineHeight = px;
      }
    }
    this.context.rootLineHeight =
      rootLineHeight ?? this.context.fontSize() * this.context.pref.lineHeight;
    this.context.isRootLineHeightFromRelativeCalc = fromRelativeCalc;
  }

  private resolveRootSizingCalc(val: Css.Val): Css.Val {
    if (!(val instanceof Css.Func)) {
      return val;
    }
    // filtering visitors always return a value
    return val.visit(
      new CssCascade.RootSizingCalcFilterVisitor(this.context),
    ) as Css.Val;
  }

  getTopContainerStyle(): CssCascade.ElementStyle {
    let offset = 0;
    while (!this.bodyReached) {
      offset += 5000;
      if (this.styleUntil(offset, 0) == Number.POSITIVE_INFINITY) {
        break;
      }
    }
    return this.rootStyle;
  }

  getAttrStyle(elem: Element): CssCascade.ElementStyle {
    // skip cases in which elements for XML other than HTML or SVG
    // have 'style' attribute not for CSS declaration
    if ((elem as any).style instanceof CSSStyleDeclaration) {
      const styleAttrValue = elem.getAttribute("style");
      if (styleAttrValue) {
        return CssCascade.parseStyleAttribute(
          this.scope,
          this.validatorSet,
          this.xmldoc.url,
          styleAttrValue,
        );
      }
    }
    return {} as CssCascade.ElementStyle;
  }

  recordCounterSnapshot(
    offset: number,
    counters: CssCascade.CounterValues,
    changes: string[] = [],
    changeTypes?: { [key: string]: "reset" | "set" | "increment" },
  ): void {
    const snapshot = {
      offset,
      counters: cloneCounterValues(counters),
      changes: Array.from(changes || []),
      changeTypes: changeTypes ? { ...changeTypes } : undefined,
    } as CounterSnapshot;
    const lastSnapshot =
      this.counterSnapshots[this.counterSnapshots.length - 1];
    // If multiple updates occur at the same offset (e.g., relayout),
    // keep the latest snapshot so counters reflect the most recent state.
    if (lastSnapshot && lastSnapshot.offset === offset) {
      this.counterSnapshots[this.counterSnapshots.length - 1] = snapshot;
      return;
    }
    this.counterSnapshots.push(snapshot);
  }

  getCounterSnapshotAtOffset(offset: number): CounterSnapshot | null {
    if (!this.counterSnapshots.length) {
      return null;
    }
    const index = Base.binarySearch(
      this.counterSnapshots.length,
      (i) => offset < this.counterSnapshots[i].offset,
    );
    const targetIndex = index === 0 ? 0 : index - 1;
    return this.counterSnapshots[targetIndex] || null;
  }

  getLastCounterSnapshot(): CounterSnapshot | null {
    return this.counterSnapshots.length
      ? this.counterSnapshots[this.counterSnapshots.length - 1]
      : null;
  }

  /**
   * @return currently reached offset
   */
  getReachedOffset(): number {
    return this.lastOffset;
  }

  /**
   * Replay flow elements that were encountered since the given offset
   */
  replayFlowElementsFromOffset(offset: number): void {
    if (offset >= this.lastOffset) {
      return;
    }
    const context = this.context;
    const rootOffset = this.xmldoc.getElementOffset(this.root);
    if (offset < rootOffset) {
      const rootStyle = this.getStyle(this.root, false);
      if (!this.cascade.firstPageType) {
        const rootPageCV = rootStyle["page"] as CssCascade.CascadeValue;
        const rootPageType =
          rootPageCV &&
          !Css.isDefaultingValue(rootPageCV.value) &&
          rootPageCV.value.toString();
        if (rootPageType && rootPageType.toLowerCase() !== "auto") {
          this.cascade.firstPageType = rootPageType;
        }
      }
      const flowName = CssCascade.getProp(rootStyle, "flow-into");
      const flowNameStr = flowName
        ? flowName.evaluate(context, "flow-into").toString()
        : "body";
      const newFlowChunk = this.encounteredFlowElement(
        flowNameStr,
        rootStyle,
        this.root,
        rootOffset,
      );
      if (this.boxStack.empty()) {
        this.boxStack.push(rootStyle, rootOffset, true, newFlowChunk);
      }
    }
    let node = this.xmldoc.getNodeByOffset(offset);
    let nodeOffset = this.xmldoc.getNodeOffset(node, 0, false);
    if (nodeOffset >= this.lastOffset) {
      return;
    }
    while (true) {
      if (node.nodeType != 1) {
        nodeOffset += node.textContent.length;
      } else {
        const elem = node as Element;
        if (VIVLIOSTYLE_DEBUG) {
          if (nodeOffset != this.xmldoc.getElementOffset(elem)) {
            throw new Error("Inconsistent offset");
          }
        }
        const style = this.getStyle(elem, false);
        const flowName = style["flow-into"] as CssCascade.CascadeValue;
        if (flowName) {
          const flowNameStr = flowName
            .evaluate(context, "flow-into")
            .toString();
          this.encounteredFlowElement(flowNameStr, style, elem, nodeOffset);
        }
        nodeOffset++;
      }
      if (nodeOffset >= this.lastOffset) {
        break;
      }
      let next: Node | null = node.firstChild;
      if (next == null) {
        while (true) {
          next = node.nextSibling;
          if (next) {
            break;
          }
          node = node.parentNode;
          if (node === this.root) {
            return;
          }
        }
      }
      node = next;
    }
  }

  resetFlowChunkStream(flowListener: FlowListener): void {
    this.flowListener = flowListener;
    for (let i = 0; i < this.flowChunks.length; i++) {
      this.flowListener.encounteredFlowChunk(
        this.flowChunks[i],
        this.flows[this.flowChunks[i].flowName],
      );
    }
  }

  styleUntilFlowIsReached(flowName: string) {
    this.flowToReach = flowName;
    let offset = 0;
    while (true) {
      if (this.flowToReach == null) {
        break;
      }
      offset += 5000;
      if (this.styleUntil(offset, 0) == Number.POSITIVE_INFINITY) {
        break;
      }
    }
  }

  styleUntilIdIsReached(id: string) {
    if (!id) {
      return;
    }
    this.idToReach = id;
    let offset = 0;
    while (true) {
      if (!this.idToReach) {
        break;
      }
      offset += 5000;
      if (this.styleUntil(offset, 0) === Number.POSITIVE_INFINITY) {
        break;
      }
    }
    this.idToReach = null;
  }

  private encounteredFlowElement(
    flowName: string,
    style: CssCascade.ElementStyle,
    elem: Element,
    startOffset: number,
  ): Vtree.FlowChunk {
    let priority = 0;
    let linger = Number.POSITIVE_INFINITY;
    let exclusive = false;
    let repeated = false;
    let last = false;
    const optionsCV = style["flow-options"] as CssCascade.CascadeValue;
    if (optionsCV) {
      const options = CssProp.toSet(
        optionsCV.evaluate(this.context, "flow-options"),
      );
      exclusive = !!options["exclusive"];
      repeated = !!options["static"];
      last = !!options["last"];
    }
    const lingerCV = style["flow-linger"] as CssCascade.CascadeValue;
    if (lingerCV) {
      linger = CssProp.toInt(
        lingerCV.evaluate(this.context, "flow-linger"),
        Number.POSITIVE_INFINITY,
      );
    }
    const priorityCV = style["flow-priority"] as CssCascade.CascadeValue;
    if (priorityCV) {
      priority = CssProp.toInt(
        priorityCV.evaluate(this.context, "flow-priority"),
        0,
      );
    }
    const breakBefore = this.breakBeforeValues[startOffset] || null;
    let flow = this.flows[flowName];
    if (!flow) {
      const parentFlowName = this.boxStack.lastFlowName();
      flow = this.flows[flowName] = new Vtree.Flow(
        flowName,
        parentFlowName,
        new LayoutProcessor.BlockFormattingContext(null),
      );
    }
    const flowChunk = new Vtree.FlowChunk(
      flowName,
      elem,
      startOffset,
      priority,
      linger,
      exclusive,
      repeated,
      last,
      breakBefore,
    );
    this.flowChunks.push(flowChunk);
    if (this.flowToReach == flowName) {
      this.flowToReach = null;
    }
    if (this.flowListener) {
      this.flowListener.encounteredFlowChunk(flowChunk, flow);
    }
    return flowChunk;
  }

  registerForcedBreakOffset(
    breakValue: string | null,
    offset: number,
    flowName: string,
  ) {
    if (Break.isForcedBreakValue(breakValue)) {
      const forcedBreakOffsets = this.flows[flowName].forcedBreakOffsets;
      if (
        forcedBreakOffsets.length === 0 ||
        forcedBreakOffsets[forcedBreakOffsets.length - 1] < offset
      ) {
        forcedBreakOffsets.push(offset);
      }
    }
    const previousValue = this.breakBeforeValues[offset];
    this.breakBeforeValues[offset] = Break.resolveEffectiveBreakValue(
      previousValue,
      breakValue,
    );
  }

  /**
   * @param startOffset current position in the document
   * @param lookup lookup window size for the next page
   * @return lookup offset in the document for the next page
   */
  styleUntil(startOffset: number, lookup: number): number {
    let targetSlippedOffset = -1;
    let slippedOffset: number;
    if (startOffset <= this.lastOffset) {
      slippedOffset = this.offsetMap.slippedByFixed(startOffset);
      targetSlippedOffset = slippedOffset + lookup;
      if (targetSlippedOffset < this.offsetMap.getMaxSlipped()) {
        // got to the desired point
        return this.offsetMap.fixedBySlipped(targetSlippedOffset);
      }
    }
    if (this.last == null) {
      return Number.POSITIVE_INFINITY;
    }
    const context = this.context;
    while (this.last) {
      let next = this.last.firstChild;
      if (next == null) {
        while (this.last) {
          if (this.last.node.nodeType == 1) {
            this.cascade.popElement(this.last.node as Element);
            this.primary = this.primaryStack.pop();
            const box = this.boxStack.pop(this.lastOffset);
            let breakAfter: string | null = null;
            if (box.afterBox) {
              const afterPseudoBreakBefore =
                box.afterBox.getBreakValue("before");
              this.registerForcedBreakOffset(
                afterPseudoBreakBefore,
                box.afterBox.atBlockStart
                  ? this.boxStack.nearestBlockStartOffset(box)
                  : box.afterBox.offset,
                box.flowName,
              );
              breakAfter = box.afterBox.getBreakValue("after");
            }
            breakAfter = Break.resolveEffectiveBreakValue(
              breakAfter,
              box.getBreakValue("after"),
            );
            this.registerForcedBreakOffset(
              breakAfter,
              this.lastOffset,
              box.flowName,
            );
          }
          next = this.last.nextSibling;
          if (next) {
            break;
          }
          this.last = this.last.parent;
          if (!this.last) {
            if (startOffset < this.lastOffset) {
              if (targetSlippedOffset < 0) {
                slippedOffset = this.offsetMap.slippedByFixed(startOffset);
                targetSlippedOffset = slippedOffset + lookup;
              }
              if (targetSlippedOffset <= this.offsetMap.getMaxSlipped()) {
                // got to the desired point
                return this.offsetMap.fixedBySlipped(targetSlippedOffset);
              }
            }
            return Number.POSITIVE_INFINITY;
          }
        }
      }
      const cursor = next;
      this.last = cursor;
      if (cursor.node.nodeType != 1) {
        this.lastOffset += cursor.node.textContent.length;
        this.boxStack.encounteredTextNode(cursor.node);
        if (this.primary) {
          this.offsetMap.addStuckRange(this.lastOffset);
        } else {
          this.offsetMap.addSlippedRange(this.lastOffset);
        }
      } else {
        const elem = cursor.node as Base.ChildElement;
        const style = this.getAttrStyle(elem);
        this.primaryStack.push(this.primary);
        this.elementWindow = this.cascade.pushElement(
          elem,
          style,
          this.lastOffset,
        );
        if (this.cascade.lastCounterChanges.length > 0) {
          this.recordCounterSnapshot(
            this.lastOffset,
            this.cascade.counters,
            this.cascade.lastCounterChanges,
            this.cascade.lastCounterChangeTypes,
          );
        }
        const id =
          elem.getAttribute("id") || elem.getAttributeNS(Base.NS.XML, "id");
        if (id && id === this.idToReach) {
          this.idToReach = null;
        }
        if (
          !this.bodyReached &&
          elem.localName == "body" &&
          elem.parentNode == this.root
        ) {
          this.postprocessTopStyle(style, true);
          this.bodyReached = true;
        }
        let box: Box;
        const flowName = style["flow-into"] as CssCascade.CascadeValue;
        if (flowName) {
          const flowNameStr = flowName
            .evaluate(context, "flow-into")
            .toString();
          const newFlowChunk = this.encounteredFlowElement(
            flowNameStr,
            style,
            elem,
            this.lastOffset,
          );
          this.primary = !!this.primaryFlows[flowNameStr];
          box = this.boxStack.push(
            style,
            this.lastOffset,
            elem === this.root,
            newFlowChunk,
          );
        } else {
          box = this.boxStack.push(style, this.lastOffset, elem === this.root);

          // For not ignoring break-before on :root (issue #666)
          if (elem === this.xmldoc.body) {
            box.breakBefore = Break.resolveEffectiveBreakValue(
              box.flowChunk.breakBefore,
              box.breakBefore,
            );
          }
        }
        const blockStartOffset = this.boxStack.nearestBlockStartOffset(box);

        if (blockStartOffset === 0 || elem === this.xmldoc.body) {
          // Named page type at first page
          const pageCV = style["page"] as CssCascade.CascadeValue;
          const pageType =
            pageCV &&
            !Css.isDefaultingValue(pageCV.value) &&
            pageCV.value.toString();
          if (
            !this.cascade.firstPageType &&
            pageType &&
            pageType.toLowerCase() !== "auto"
          ) {
            this.cascade.firstPageType = pageType;
          }
        }

        this.registerForcedBreakOffset(
          box.breakBefore,
          blockStartOffset,
          box.flowName,
        );
        if (box.beforeBox) {
          const beforePseudoBreakAfter = box.beforeBox.getBreakValue("after");
          this.registerForcedBreakOffset(
            beforePseudoBreakAfter,
            box.beforeBox.atBlockStart ? blockStartOffset : box.offset,
            box.flowName,
          );
        }
        if (this.primary) {
          if (box.displayValue() === Css.ident.none) {
            this.primary = false;
          }
        }
        if (VIVLIOSTYLE_DEBUG) {
          const offset = this.xmldoc.getElementOffset(elem);
          if (offset != this.lastOffset) {
            throw new Error("Inconsistent offset");
          }
        }
        this.styles.setAt(this.lastOffset, style);
        this.lastOffset++;
        if (this.primary) {
          this.offsetMap.addStuckRange(this.lastOffset);
        } else {
          this.offsetMap.addSlippedRange(this.lastOffset);
        }
        if (this.bodyReached && blockStartOffset === 0) {
          // body reached but the named page type at first page is not determined
          // (Fix for issue #770)
          continue;
        }
        if (startOffset < this.lastOffset) {
          if (targetSlippedOffset < 0) {
            slippedOffset = this.offsetMap.slippedByFixed(startOffset);
            targetSlippedOffset = slippedOffset + lookup;
          }
          if (targetSlippedOffset <= this.offsetMap.getMaxSlipped()) {
            // got to the desired point
            return this.offsetMap.fixedBySlipped(targetSlippedOffset);
          }
        }
      }
    }
    return Number.POSITIVE_INFINITY;
  }

  /** @override */
  getStyle(element: Element, deep: boolean): CssCascade.ElementStyle {
    let offset = this.xmldoc.getElementOffset(element);
    if (deep) {
      offset = this.xmldoc.getNodeOffset(element, 0, true);
    }
    if (this.lastOffset <= offset) {
      this.styleUntil(offset, 0);
    }
    return this.styles.styleOf(element);
  }

  /** @override */
  processContent(
    element: Element,
    styles: { [key: string]: Css.Val },
    nodeContext: Vtree.NodeContext,
  ) {}
}

export const columnProps = ["column-count", "column-width", "column-fill"];
